export type UsPublicRoute = {
  globalPath: string;
  publicPath: string;
  internalPath: string;
  metadataSlug: string;
  legacyPaths?: readonly string[];
};

export const US_PUBLIC_ROUTES: readonly UsPublicRoute[] = [
  {
    globalPath: '/',
    publicPath: '/us',
    internalPath: '/us',
    metadataSlug: 'us',
  },
  {
    globalPath: '/performance-marketing',
    publicPath: '/performance-marketing/us',
    internalPath: '/us/performance-marketing',
    metadataSlug: 'performance-marketing/us',
    legacyPaths: ['/performance-marketing/usa'],
  },
] as const;

export const US_SITE_ROUTES: readonly string[] = US_PUBLIC_ROUTES
  .map((route) => route.globalPath)
  .filter((path) => path !== '/');

export function normalizeUsPath(path: string): string {
  const withoutQuery = path.split(/[?#]/)[0] || '/';
  const withLeadingSlash = withoutQuery.startsWith('/')
    ? withoutQuery
    : `/${withoutQuery}`;

  if (withLeadingSlash.length > 1 && withLeadingSlash.endsWith('/')) {
    return withLeadingSlash.slice(0, -1);
  }

  return withLeadingSlash;
}

function stripLeadingSlash(path: string): string {
  return path.replace(/^\/+/, '');
}

function findByGlobalPath(path: string): UsPublicRoute | undefined {
  const normalized = normalizeUsPath(path);
  return US_PUBLIC_ROUTES.find((route) => route.globalPath === normalized);
}

function findByPublicPath(path: string): UsPublicRoute | undefined {
  const normalized = normalizeUsPath(path);
  return US_PUBLIC_ROUTES.find((route) => route.publicPath === normalized);
}

function findByInternalPath(path: string): UsPublicRoute | undefined {
  const normalized = normalizeUsPath(path);
  return US_PUBLIC_ROUTES.find((route) => route.internalPath === normalized);
}

function findByLegacyPath(path: string): UsPublicRoute | undefined {
  const normalized = normalizeUsPath(path);
  return US_PUBLIC_ROUTES.find((route) =>
    route.legacyPaths?.includes(normalized)
  );
}

export function getPublicUsPath(globalPath: string): string | null {
  return findByGlobalPath(globalPath)?.publicPath || null;
}

export function getPublicUsHref(currentPath: string, href: string): string {
  const isUsContext = findByPublicPath(currentPath) || normalizeUsPath(currentPath) === '/us';
  if (!isUsContext) return href;
  return getPublicUsPath(href) || href;
}

export function getInternalUsRewritePath(publicPath: string): string | null {
  const route = findByPublicPath(publicPath);
  if (!route || route.internalPath === route.publicPath) return null;
  return route.internalPath;
}

export function getCanonicalPathForLegacyUsPath(path: string): string | null {
  const internalRoute = findByInternalPath(path);
  if (internalRoute && internalRoute.internalPath !== internalRoute.publicPath) {
    return internalRoute.publicPath;
  }

  return findByLegacyPath(path)?.publicPath || null;
}

export function isPrivateUsImplementationPath(path: string): boolean {
  const route = findByInternalPath(path);
  return Boolean(route && route.internalPath !== route.publicPath);
}

export function mapAppSlugToPublicSlug(slug: string): string {
  const normalizedSlug = stripLeadingSlash(slug || '');
  if (!normalizedSlug) return '';

  const normalizedPath = normalizeUsPath(normalizedSlug);
  const route =
    findByInternalPath(normalizedPath) ||
    findByLegacyPath(normalizedPath) ||
    findByPublicPath(normalizedPath);

  return stripLeadingSlash(route?.publicPath || normalizedSlug);
}

export function isPrivateUsImplementationSlug(slug: string): boolean {
  return isPrivateUsImplementationPath(normalizeUsPath(slug));
}
